import { superstructResolver } from "@hookform/resolvers/superstruct";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InfoIcon from "@mui/icons-material/Info";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { CreateConnectionRequest, ExternalProject } from "@snubes/snubes-types";
import { FC, useCallback, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { array, Infer, nullable, object, string, type } from "superstruct";
import { handleError } from "../../Common/helpers/handleError";
import { isProjectMappable } from "../../Common/helpers/isProjectMappable";
import { ControlledAutocomplete } from "../../Form/views/ControlledAutocomplete";
import { ControlledProjectIdAutocomplete } from "../../Form/views/ControlledProjectIdAutocomplete";
import { ControlledTextField } from "../../Form/views/ControlledTextField";
import { useT } from "../../Translation/hooks/useT";
import { createConnectorConfigCallable } from "../callables/createConnectorConfigCallable";
import { testConnectorConfigVccLiveCallable } from "../callables/testConnectorConfigVccLiveCallable";
import { haveProjectMappingsDuplicates } from "../helpers/haveProjectMappingsDuplicates";

const FormValuesStruct = type({
  userId: string(),
  password: string(),
  name: string(),
  customerId: string(),
  projectMappings: array(
    object({
      projectId: nullable(string()),
      externalProjectId: nullable(string()),
      projectName: string(),
    }),
  ),
});

type FormValues = Infer<typeof FormValuesStruct>;

export const ConnectorConfigVccLiveForm: FC = () => {
  const t = useT();
  const navigate = useNavigate();
  const { organizationId } = useParams<"organizationId">();

  const [errorMessage, setErrorMessage] = useState<string>();
  const [externalProjects, setExternalProjects] = useState<
    ExternalProject[] | undefined
  >();

  const { control, formState, handleSubmit, setValue } = useForm<FormValues>({
    resolver: superstructResolver(FormValuesStruct),
    defaultValues: {
      userId: "",
      name: "",
      password: "",
      customerId: "",
      projectMappings: [
        { projectId: null, externalProjectId: null, projectName: "" },
      ],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "projectMappings",
  });

  const handleProjectMappingRemove = (index: number) => {
    remove(index);
  };
  const handleProjectMappingAppend = () => {
    append({ projectId: null, externalProjectId: null, projectName: "" });
  };

  const testConnectorVccLiveConfig = useCallback(
    async (formValues: FormValues, organizationId: string) => {
      try {
        const data = await testConnectorConfigVccLiveCallable({
          userId: formValues.userId,
          password: formValues.password,
          organizationId,
          customerId: formValues.customerId,
          platform: "VCC_LIVE",
        });

        setErrorMessage(undefined);
        setExternalProjects(data.externalProjects);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : String(error));
      }
    },
    [],
  );

  const createConnectorConfig = useCallback(
    async (formValues: FormValues, organizationId: string) => {
      try {
        const projectMappings = formValues.projectMappings.reduce<
          CreateConnectionRequest["projectMappings"]
        >((result, mapping) => {
          const { projectId, externalProjectId, projectName } = mapping;
          if (projectId && externalProjectId) {
            result.push({
              projectId,
              externalProjectId,
              projectName,
            });
          }
          return result;
        }, []);

        await createConnectorConfigCallable({
          name: formValues.name,
          organizationId,
          platform: "VCC_LIVE",
          userId: formValues.userId,
          password: formValues.password,
          customerId: formValues.customerId,
          projectMappings,
        });
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : String(error));
      }
    },
    [],
  );

  const onSubmit = useCallback(
    async (formValues: FormValues) => {
      try {
        if (haveProjectMappingsDuplicates(formValues.projectMappings)) {
          throw new Error(
            t("ConnectorConfigVccLiveForm_Toast_MultipleProjects"),
          );
        }
        if (!organizationId) {
          throw new Error("No organization selected");
        }

        if (externalProjects) {
          await createConnectorConfig(formValues, organizationId);
          navigate(`/organizations/${organizationId}/connector-configs`);
        } else {
          await testConnectorVccLiveConfig(formValues, organizationId);
        }
      } catch (error) {
        handleError(error).logAnd().toast();
      }
    },
    [
      createConnectorConfig,
      externalProjects,
      navigate,
      organizationId,
      testConnectorVccLiveConfig,
      t,
    ],
  );

  const { isSubmitting } = formState;
  const isConnecting = isSubmitting && !externalProjects;

  return (
    <Stack pt={3}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <ControlledTextField
            autoComplete="off"
            required
            fullWidth
            name="name"
            label={t("ConnectorConfigVccLiveForm_InputLabel_Name")}
            control={control}
            disabled={isSubmitting}
          />
          <Divider />

          <Typography textTransform="uppercase" variant="subtitle1">
            {t("ConnectorConfigVccLiveForm_SubHeading_Credentials")}
          </Typography>

          <ControlledTextField
            required
            autoComplete="off"
            fullWidth
            name="userId"
            label={t("ConnectorConfigVccLiveForm_InputLabel_User")}
            control={control}
            disabled={isSubmitting}
          />

          <ControlledTextField
            required
            autoComplete="off"
            fullWidth
            name="password"
            type="password"
            label={t("ConnectorConfigVccLiveForm_InputLabel_Password")}
            control={control}
            disabled={isSubmitting}
          />
          <ControlledTextField
            required
            autoComplete="off"
            fullWidth
            name="customerId"
            label={t("ConnectorConfigVccLiveForm_InputLabel_Customer")}
            control={control}
            disabled={isSubmitting}
          />
          {isConnecting && (
            <Stack spacing={3} alignItems="center" direction="row">
              <CircularProgress size={20} />
              <Typography variant="subtitle2">
                {t("ConnectorConfigVccLiveForm_SubHeading_ConnectingVcc")}
              </Typography>
            </Stack>
          )}
          {!!errorMessage && (
            <Stack color="error.main" spacing={3}>
              <Stack spacing={3} alignItems="center" direction="row">
                <InfoIcon />
                <Typography variant="subtitle2">
                  {t("ConnectorConfigVccLiveForm_SubHeading_ConnectedError")}
                </Typography>
              </Stack>
              <Stack
                p={5}
                bgcolor="background.default"
                justifyContent="center"
                alignContent="center"
              >
                {errorMessage}
              </Stack>
              <Divider />
            </Stack>
          )}
          {externalProjects && (
            <>
              <Stack spacing={3}>
                <Stack
                  color="success.main"
                  spacing={3}
                  alignItems="center"
                  direction="row"
                >
                  <CheckCircleIcon />
                  <Typography variant="subtitle2">
                    {t("ConnectorConfigVccLiveForm_SubHeading_Connected")}
                  </Typography>
                </Stack>
                <Divider />
                <Typography
                  py={3}
                  textTransform="uppercase"
                  variant="subtitle1"
                >
                  {t("ConnectorConfigVccLiveForm_SubHeading_ChooseProject")}
                </Typography>
                <Stack
                  justifyContent="space-between"
                  alignItems="center"
                  direction="row"
                  spacing={3}
                >
                  <Stack flex={1}>
                    <Typography variant="subtitle1">
                      {t("ConnectorConfigVccLiveForm_Label_VccProject")}
                    </Typography>
                  </Stack>
                  <Stack flex={1}>
                    <Typography variant="subtitle1">
                      {t("ConnectorConfigVccLiveForm_Label_SnubesProject")}
                    </Typography>
                  </Stack>
                </Stack>
                <Divider />

                {fields.map((item, index) => (
                  <Stack
                    key={item.id}
                    justifyContent="space-between"
                    alignItems="flex-start"
                    direction="row"
                    spacing={3}
                  >
                    {externalProjects.length > 0 && (
                      <Stack flex={1}>
                        <ControlledAutocomplete
                          fullWidth
                          required
                          name={`projectMappings.${index}.externalProjectId`}
                          control={control}
                          options={externalProjects.map((p) => p.id)}
                          onChange={(externalProjectId) => {
                            setValue(
                              `projectMappings.${index}.projectName`,
                              externalProjects.find(
                                (f) => f.id === externalProjectId,
                              )?.name || "",
                            );
                          }}
                          getOptionLabel={(projectId) =>
                            externalProjects.find(
                              (project) => project.id === projectId,
                            )?.name || ""
                          }
                          label={t(
                            "ConnectorConfigVccLiveForm_SelectLabel_ChooseVccProject",
                          )}
                        />
                      </Stack>
                    )}
                    {organizationId && (
                      <Stack flex={1} spacing={3}>
                        <ControlledProjectIdAutocomplete
                          fullWidth
                          required
                          filter={(project) => isProjectMappable(project)}
                          name={`projectMappings.${index}.projectId`}
                          projectNameFieldName={`projectMappings.${index}.projectName`}
                          control={control}
                          label={t(
                            "ConnectorConfigVccLiveForm_SelectLabel_SnubesProject",
                          )}
                          organizationId={organizationId}
                        />
                      </Stack>
                    )}

                    {(index !== 0 || fields.length > 1) && (
                      <IconButton
                        sx={{ p: 0 }}
                        onClick={() => {
                          handleProjectMappingRemove(index);
                        }}
                      >
                        <DeleteForeverIcon sx={{ color: "error.main" }} />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>

              <Stack
                borderTop={1}
                borderBottom={1}
                py={3}
                px={2}
                bgcolor="background.default"
                borderColor="divider"
                alignItems="center"
                direction="row"
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={handleProjectMappingAppend}
                  startIcon={<AddCircleIcon />}
                >
                  {t("ConnectorConfigVccLiveForm_Button_NewMapping")}
                </Button>
              </Stack>
            </>
          )}
          <Button
            type="submit"
            variant="contained"
            color={errorMessage ? "error" : undefined}
            disabled={isSubmitting || isConnecting}
          >
            {!errorMessage &&
              !externalProjects &&
              t("ConnectorConfigVccLiveForm_Button_Connect")}
            {externalProjects &&
              t("ConnectorConfigVccLiveForm_Button_FinishSetUp")}
            {!!errorMessage && t("ConnectorConfigVccLiveForm_Button_TryAgain")}
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};
